import { formatCurrency } from "@sa-nu/shared-utils/price";
import clsx from "clsx";

import { useGtm } from "../../../../../lib/google-tag-manager/gtm";
import { useRoomDetailDrawer } from "../../../room-detail/room-detail-drawer-provider";
import { useOptionalPendingStaySelectionContext } from "../../../site-detail/_context/pending-stay-selection-context";
import { useRoomTypeReserveContext } from "../../_context/room-type-reserve-context";
import type { BrowseRoomTypeWithAvailability } from "../../_hooks/use-room-type-search";
import { isRoomTypeUnavailable } from "../../_utils/room-type-availability-view";
import { buildRoomDetailPayloadForReserve } from "../../reserve/build-room-detail-payload-for-reserve";
import { ReserveRoomTypePill } from "../../ui/reserve-room-type-pill";

import { FeatureSubtext } from "./_components/feature-subtext";
import { PriceSkeleton } from "./price-skeleton";

/**
 * 建築タイプグループ内の部屋タイプ 1 行（リーフ）。Figma（6190-5662 Item/RoomItem）準拠。
 * 行頭に薄い仕切り線を表示する（建築タイプ内の先頭行のみ `first:hidden` で非表示）。
 */
function Divider() {
  return (
    <div className="px-[16px] first:hidden">
      <div className="border-dx-surface-subtle border-t" />
    </div>
  );
}

export function RoomTypeRow({
  roomType,
}: {
  roomType: BrowseRoomTypeWithAvailability;
}) {
  const { open } = useRoomDetailDrawer();
  const gtm = useGtm();
  const reserveCtx = useRoomTypeReserveContext();
  // Provider の外（SiteCard 経由のブラウズ一覧）から呼ばれると null。
  // 拠点詳細ドロワー（SiteDetailBody）経由のときだけ non-null になる。
  const pendingStayCtx = useOptionalPendingStaySelectionContext();
  // availabilities が未取得（二段フェッチの構造フェーズ）なら価格読み込み中。
  const priceLoading = roomType.availabilities === undefined;
  const fromPrice = roomType.availabilities?.fromPrice ?? null;
  const unavailable = isRoomTypeUnavailable(roomType);

  const handleOpen = () => {
    gtm.sendEvent("rt_rsv_card_reserve_clicked", { room_type_id: roomType.id });
    if (reserveCtx === null) {
      open({ roomTypeId: roomType.id });
      return;
    }
    // 行タップは詳細を開くだけの操作のため URL（ci/co）は書き換えず、選択中の一時日程は
    // payload のスナップショットとして渡す（URL反映は「予約に進む」ボタン押下に委ねる）。
    // 満室の部屋タイプは日程フィルタを引き継がず、空いている日を確認しやすくする。
    const ctxForOpen = unavailable
      ? { ...reserveCtx, firstNight: null, lastNight: null }
      : pendingStayCtx !== null
        ? {
            ...reserveCtx,
            firstNight: pendingStayCtx.firstNight,
            lastNight: pendingStayCtx.lastNight,
          }
        : reserveCtx;
    open(buildRoomDetailPayloadForReserve(roomType.id, ctxForOpen));
  };

  return (
    <>
      <Divider />
      <button
        data-analytics-id="room_type_browse_opened_room_type"
        type="button"
        data-testid={unavailable ? "room-type-row-unavail" : "room-type-row"}
        onClick={handleOpen}
        className={clsx(
          "bg-dx-surface-base flex w-full items-start gap-[16px] px-[16px] pt-[8px] pb-[4px] text-left",
          "hover:bg-dx-bg-base-hover cursor-pointer transition-colors duration-150",
        )}
      >
        <div className="flex min-w-px flex-1 flex-col gap-[4px] pt-[4px]">
          {roomType.displayName && (
            <p className="font-notojp text-dx-text-default text-[16px] leading-[24px] font-bold">
              {roomType.displayName}
            </p>
          )}
          <FeatureSubtext
            roomType={roomType}
            evChargerAvailable={reserveCtx?.evChargerAvailable ?? false}
          />
          {priceLoading ? (
            <PriceSkeleton />
          ) : unavailable ? (
            <p className="flex flex-wrap items-center gap-x-[4px]">
              <span className="font-notojp text-dx-text-sub text-[16px] leading-[28px] font-bold">
                ¥ ---
              </span>
              <span className="font-notojp text-dx-text-default text-[12px] leading-[18px]">
                /1泊
              </span>
            </p>
          ) : (
            fromPrice != null && (
              <p className="flex flex-wrap items-center gap-x-[4px]">
                <span className="font-notojp text-dx-text-sub text-[16px] leading-[28px] font-bold">
                  {formatCurrency(fromPrice)}〜
                </span>
                <span className="font-notojp text-dx-text-default text-[12px] leading-[18px]">
                  /1泊2名
                </span>
              </p>
            )
          )}
        </div>

        {/* 詳細・予約/部屋詳細ピル（ReserveRoomTypePill が unavailable で variant・ラベルを切替） */}
        <div className="flex shrink-0 flex-col items-center justify-center">
          <ReserveRoomTypePill unavailable={unavailable} />
        </div>
      </button>
    </>
  );
}
